import {
  Box, Checkbox, Flex, FormControl, FormErrorMessage, Radio, RadioGroup, Select, Text,
  Tooltip
} from '@chakra-ui/react'
import type { PolicyHolderAdditionalData, WizardVehicleAdditionalData } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'brokertslib/src/enums'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { useContext, useMemo } from 'react'

import { CheckboxFormControl, RadioGroupFormControl } from 'BrokerTsLib/src/components'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import { FormSpacer } from '../components/FormSpacer'
import { InputGroup } from '../components/InputGroup'
import { Panel } from '../components/Panel'
import { RadioStack } from '../components/RadioStack'
import { useSelections } from '../hooks'
import { GetFormItemName, useNameof } from '../hooks/useNameof'
import { Info } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'
import { Step4Context } from '../pages/carkulka/wizard/4'
import { BCNOptions } from './BCNOptions'
import { ICOOptions } from './ICOOptions'
import { InsureeAddressRuian } from './InsureeAddressRuian'
import InsureeContact from './InsureeContact'
import { InsureeName } from './InsureeName'
import { InsureeType } from './InsureeType'
import InsureeVinkulant from './InsureeVinkulant'

export const AdditionalInsureeInfo = () => {
  const { insuranceParams } = useContext(insuranceParamsContext)
  const { offer } = useContext(Step4Context)
  const { data: dataTitles, isLoading: isLoadingTitles, isFetching: isFetchingTitles } = useSelections(SelectionType.TitleBefore)

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const { register, watch, control, getFieldState, getValues } = useFormContext<WizardVehicleAdditionalData>()
  const nameof = useNameof<WizardVehicleAdditionalData>()
  const phnameof = useNameof<PolicyHolderAdditionalData>(nameof(x => x.policyHolder))

  const holderSameAs = watch('policyHolder.holderSameAs')
  const ownerSameAs = watch('policyHolder.ownerSameAs')
  const contactSameAs = watch('policyHolder.contactSameAs')

  const sameAsOptions = useMemo(() => [
    { label: 'Stejný s pojistníkem', value: Selection.PersonSameAsPolicyHolder },
    { label: 'Stejný s držitelem/provozovatelem', value: Selection.PersonSameAsKeeper },
    { label: 'Stejný s vlastníkem', value: Selection.PersonSameAsOwner },
    { label: 'Jiný', value: null },
  ], [])

  const { error: agreementError } = getFieldState('policyHolder.person.agreement')
  const { error: agreementOtherError } = getFieldState('policyHolder.person.agreementOther')

  const phType = watch('policyHolder.person.type')
  const holderType = watch('policyHolder.holder.type')
  const ownerType = watch('policyHolder.owner.type')
  const contactType = watch('policyHolder.contact.type')
  const vinkulantExist = watch('policyHolder.vinkulantExists')
  const contactWarning = getValues('policyHolder.contactWarningMessage')
  const agreementWarning = getValues('policyHolder.agreementWarningMessage')

  return (
    <>
      {/* policy holder */}
      <>
        <InsureeType personPrefix={GetFormItemName<WizardVehicleAdditionalData>(x => x.policyHolder.person)} disabled />

        <FormSpacer />

        {isIn(phType, Selection.Company, Selection.SelfEmployed) &&
          <CheckboxFormControl name='policyHolder.person.isVatPayer' control={control} isDisabled={true}
            label="Plátce DPH" mb={4} />
        }

        {isIn(phType, Selection.Person, Selection.SelfEmployed) &&
          <>
            <BCNOptions personPrefix={GetFormItemName<WizardVehicleAdditionalData>(x => x.policyHolder.person)} disabled />
            <FormGroup>
              <FormControl>
                <FormLabel>Titul před jménem</FormLabel>

                {(dataTitles?.length ?? 0) > 0 &&
                  <Select
                    {...register('policyHolder.person.titleBefore')}
                    isDisabled={isContracted}
                    defaultValue={getValues('policyHolder.person.titleBefore') ?? ''}
                  >
                    <option value=''>Vyberte</option>
                    {dataTitles?.map((item) => (
                      <option value={item.value?.toString()} key={item.id}>{item.value}</option>
                    ))}
                  </Select>
                }
              </FormControl>

              <InsureeName personPrefix={GetFormItemName<WizardVehicleAdditionalData>(x => x.policyHolder.person)} isDisabled />

              <FormControl>
                <FormLabel>Titul za jménem</FormLabel>

                <InputGroup placeholder="Např. PhD." htmlSize={8} {...register('policyHolder.person.titleAfter')} isDisabled={isContracted} />
              </FormControl>
            </FormGroup>
          </>
        }

        {isIn(phType, Selection.Company, Selection.SelfEmployed) && <ICOOptions personPrefix={GetFormItemName<WizardVehicleAdditionalData>(x => x.policyHolder.person)} disabled showAgent={getValues('policyHolder.person.agent.required') ?? false} />}

        <FormSpacer />

        <InsureeAddressRuian addressPrefix={GetFormItemName<WizardVehicleAdditionalData>(x => x.policyHolder!.person!.address)} isDisabled />

        <InsureeContact personPrefix={phnameof(x => x.person)} isDisabled={isContracted}
          mobileRequired={offer.insurerId == Insurer.Kooperativa}
          emailRequired={offer.insurerId == Insurer.Kooperativa}
        />

        <FormControl isInvalid={!!agreementError?.message}>
          <Flex sx={{ gap: 2 }}>
            <Checkbox {...register('policyHolder.person.agreement')} isDisabled={isContracted}>
              {offer.insurerId == Insurer.GeneraliCeskaPojistovna && 'Souhlas s el. komunikací při jednání o uzavření pojistné smlouvy'}
              {offer.insurerId != Insurer.GeneraliCeskaPojistovna && 'Souhlas s elektronickou komunikací'}
            </Checkbox>
            <Tooltip
              shouldWrapChildren
              hasArrow
              label="Souhlas s elektronickou komunikací"
              placement="top"
            >
              <Info color="blue.500" />
            </Tooltip>
          </Flex>
          {agreementWarning &&
            <Box >
              <Text variant='warning' fontSize={'small'}>{agreementWarning}</Text>
            </Box>
          }
          {<FormErrorMessage>{agreementError?.message}</FormErrorMessage>}
        </FormControl>

        {offer.insurerId == Insurer.GeneraliCeskaPojistovna &&
          <FormControl isInvalid={!!agreementOtherError?.message}>
            <Flex sx={{ gap: 2 }}>
              <Checkbox {...register('policyHolder.person.agreementOther')} isDisabled={isContracted}>
                Souhlas s el. komunikací během trvání pojištění
              </Checkbox>
              <Tooltip
                shouldWrapChildren
                hasArrow
                label={<Box dangerouslySetInnerHTML={{ __html: 'Klient souhlasí a požaduje odesílání informací a DOKUMENTŮ BĚHEM TRVÁNÍ POJIŠTĚNÍ A PŘI ZMĚNĚ PS elektronicky (e-mailem příp. SMS).<br/><br/>Při nesouhlasu budou DOKUMENTY BĚHEM TRVÁNÍ POJIŠTĚNÍ A PŘI ZMĚNĚ PS zaslány v listinné podobě.<br/><br/>POZOR: nesouhlas ovlivňuje všechny klientovy PS (aktivní i budoucí). V případě nesouhlasu, NEBUDOU klientovi odcházet e-maily ani SMS na žádnou z aktivních PS v NŽP i ŽP.<br/><br/>DOKUMENTY BĚHEM TRVÁNÍ POJIŠTĚNÍ jsou například Upozornění na splatnost, Výroční dopis, Upozornění na chybějící platbu A PŘI ZMĚNĚ PS například Informace o pojišťovně, produktu, nebo zprostředkovateli, Záznam z jednání , Pojistnou smlouvu a další dokumenty.' }}></Box>}
                placement="top"
              >
                <Info color="blue.500" />
              </Tooltip>
            </Flex>
            {<FormErrorMessage>{agreementOtherError?.message}</FormErrorMessage>}
          </FormControl>
        }
      </>

      <FormSpacer />

      {/* DRZITEL */}
      <RadioGroupFormControl
        name='policyHolder.holderSameAs'
        control={control}
        label='Držitel/provozovatel vozidla'
        labelProps={{ hint: 'Držitel/provozovatel vozidla', hintType: 'okapps' }}
        valueAsNumber
        zeroAsNull
        isDisabled
      >
        <RadioStack variant="stackedTile">
          {
            sameAsOptions
              .filter(item => isIn(item.value, Selection.PersonSameAsPolicyHolder, null))
              .map(item => (
                <Radio key={item.value} value={item.value?.toString() ?? '0'}>
                  {item.label}
                </Radio>
              ))
          }
        </RadioStack>
      </RadioGroupFormControl>

      {/* HOLDER OTHER */}
      {
        !holderSameAs && <Panel>
          <InsureeType personPrefix={phnameof(x => x.holder)} disabled />

          <FormSpacer />

          {isIn(holderType, Selection.Company, Selection.SelfEmployed) &&
            <CheckboxFormControl name='policyHolder.holder.isVatPayer' control={control} isDisabled={true} label='Plátce DPH' mb={4} />
          }

          {isIn(holderType, Selection.Person, Selection.SelfEmployed) && <BCNOptions personPrefix={phnameof(x => x.holder)} />}
          {isIn(holderType, Selection.Company, Selection.SelfEmployed) && <ICOOptions personPrefix={phnameof(x => x.holder)} disabled showAgent={getValues('policyHolder.holder.agent.required') ?? false} />}

          {isIn(holderType, Selection.Person, Selection.SelfEmployed) &&
            <FormGroup>
              <FormControl>
                <FormLabel>Titul před jménem</FormLabel>

                <InputGroup placeholder="Např. Ing." htmlSize={8} {...register('policyHolder.holder.titleBefore')} isDisabled={isContracted} />
              </FormControl>

              <InsureeName personPrefix={phnameof(x => x.holder)} isDisabled />

              <FormControl>
                <FormLabel>Titul za jménem</FormLabel>

                <InputGroup placeholder="Např. PhD." htmlSize={8} {...register('policyHolder.holder.titleAfter')} isDisabled={isContracted} />
              </FormControl>
            </FormGroup>
          }

          <FormSpacer />

          <InsureeAddressRuian addressPrefix={phnameof(x => x.holder!.address)} isDisabled />

          <FormSpacer />

          <InsureeContact personPrefix={phnameof(x => x.holder)} isDisabled={isContracted} />
        </Panel>
      }

      {/* OWNER */}
      <FormControl>
        <FormLabel tooltip="Vlastník vozidla">Vlastník vozidla</FormLabel>

        <Controller
          name='policyHolder.ownerSameAs'
          control={control}
          render={({ field: { onChange, value } }) => (
            <RadioGroup onChange={e => onChange(!e ? e : parseInt(e))} value={value?.toString() ?? ''} isDisabled>
              <RadioStack variant="stackedTile">
                {
                  sameAsOptions
                    .filter(item => isIn(item.value, Selection.PersonSameAsPolicyHolder, Selection.PersonSameAsKeeper, null))
                    .map(item => (
                      <Radio key={item.value} value={item.value?.toString() ?? ''}>
                        {item.label}
                      </Radio>
                    ))
                }
              </RadioStack>
            </RadioGroup>
          )}
        />
      </FormControl>

      {!ownerSameAs &&
        <Panel>
          <InsureeType personPrefix={phnameof(x => x.owner)} disabled />

          <FormSpacer />

          {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
            <CheckboxFormControl name='policyHolder.owner.isVatPayer' control={control} isDisabled={true} label='Plátce DPH' mb={4} />
          }

          {isIn(ownerType, Selection.Person, Selection.SelfEmployed) &&
            <>
              <BCNOptions personPrefix={phnameof(x => x.owner)} />

              <FormGroup>
                <FormControl>
                  <FormLabel>Titul před jménem</FormLabel>

                  <InputGroup placeholder="Např. Ing." htmlSize={8} {...register('policyHolder.owner.titleBefore')} isDisabled={isContracted} />
                </FormControl>

                <InsureeName personPrefix={phnameof(x => x.owner)} isDisabled />

                <FormControl>
                  <FormLabel>Titul za jménem</FormLabel>

                  <InputGroup placeholder="Např. PhD." htmlSize={8} {...register('policyHolder.owner.titleAfter')} isDisabled={isContracted} />
                </FormControl>
              </FormGroup>
            </>
          }

          {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
            <ICOOptions personPrefix={phnameof(x => x.owner)} disabled showAgent={getValues('policyHolder.owner.agent.required') ?? false} />
          }
          <FormSpacer />

          <InsureeAddressRuian addressPrefix={phnameof(x => x.owner!.address)} isDisabled />

          <FormSpacer />

          <InsureeContact personPrefix={phnameof(x => x.owner)} isDisabled={isContracted} />
        </Panel>
      }

      {/* CONTACT */}
      <FormControl>
        <FormLabel tooltip="Korespondenční adresa">Korespondenční adresa</FormLabel>

        <Controller
          name='policyHolder.contactSameAs'
          control={control}
          render={({ field: { onChange, value } }) => (
            <RadioGroup onChange={e => onChange(!e ? e : parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
              <RadioStack variant="stackedTile">
                {
                  sameAsOptions
                    .map(item => (
                      <Radio key={item.value} value={item.value?.toString() ?? ''}>
                        {item.label}
                      </Radio>
                    ))
                }
              </RadioStack>
            </RadioGroup>
          )}
        />
      </FormControl>

      {
        !contactSameAs && <Panel>
          <InsureeType personPrefix={phnameof(x => x.contact)} />

          <FormSpacer />


          {isIn(contactType, Selection.Person, Selection.SelfEmployed) &&
            <>
              <FormGroup>
                <FormControl>
                  <FormLabel>Titul před jménem</FormLabel>

                  <InputGroup placeholder="Např. Ing." htmlSize={8} {...register('policyHolder.contact.titleBefore')} isDisabled={isContracted} />
                </FormControl>

                <InsureeName personPrefix={phnameof(x => x.contact)} isDisabled={isContracted} />

                <FormControl>
                  <FormLabel>Titul za jménem</FormLabel>

                  <InputGroup placeholder="Např. PhD." htmlSize={8} {...register('policyHolder.contact.titleAfter')} isDisabled={isContracted} />
                </FormControl>
              </FormGroup>
            </>
          }

          {isIn(contactType, Selection.Company, Selection.SelfEmployed) &&
            <ICOOptions personPrefix={phnameof(x => x.contact)} showAgent={false} hideIc={true} />
          }

          <FormSpacer />

          <InsureeAddressRuian addressPrefix={phnameof(x => x.contact!.address)} isDisabled={isContracted} />

        </Panel>
      }

      {contactWarning &&
        <Box >
          <Text variant='warning' fontSize={'small'}>{contactWarning}</Text>
        </Box>
      }

      {getValues('policyHolder.showVinkulant') &&
        <>
          <FormControl>
            <FormLabel tooltip="Vinkulant">Vinkulant</FormLabel>

            <Controller
              name='policyHolder.vinkulantExists'
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup onChange={e => onChange(e == 'true')} value={value?.toString() ?? ''} isDisabled={isContracted}>
                  <RadioStack variant="stackedTile">
                    <Radio value="false">
                      Neuveden
                    </Radio>
                    <Radio value="true">
                      Uvést vinkulanta
                    </Radio>
                  </RadioStack>
                </RadioGroup>
              )} />

          </FormControl>

          {
            vinkulantExist && <Panel>
              <InsureeVinkulant vinkulantPrefix={phnameof(x => x.vinkulant)} isDisabled={isContracted} />
            </Panel>
          }
        </>
      }
    </>
  )
}
